import { AttachFile, Comment, Event, SwapCalls } from '@tempo-io/sui-icons';
import type { ReactNode } from 'react';
import { useSwimlaneRootProps } from '@/components/ui/swimlane/context';
import type { ICard } from '@/components/ui/swimlane/types';
import { SubItems } from '../subitems/SubItems';

import { CardButton } from './CardButton';

type ButtonRowProps = Pick<
  ICard,
  | 'itemID'
  | 'rowID'
  | 'columnID'
  | 'linkedItems'
  | 'keyDates'
  | 'comments'
  | 'attachments'
  | 'subItems'
> & {
  actionsExpanded: boolean;
  getSubItemsButton: (canHaveLabel: boolean) => ReactNode | null;
  canAddSubItems?: boolean;
  onToggleExpanded: () => void;
  subItemsBackgroundColor?: string;
};

export function ButtonRow({
  itemID,
  rowID,
  columnID,
  linkedItems = 0,
  keyDates = 0,
  comments = 0,
  attachments = 0,
  subItems = [],
  actionsExpanded,
  getSubItemsButton,
  onToggleExpanded,
  subItemsBackgroundColor,
}: ButtonRowProps) {
  const {
    actions,
    isPublicationMode,
    readOnly,
    isCompact,
    maxSubItemsPerItem,
    isSubItemFeaturesEnabled,
    isSubItemsVisible,
  } = useSwimlaneRootProps();
  const maxSubItems = maxSubItemsPerItem as number;
  const itemButtonActions = actions?.itemButtonActions || {};
  const { linkedItemsHandler, keyDatesHandler, commentsHandler, attachmentsHandler } =
    itemButtonActions;
  const shouldShowSubItems = isSubItemFeaturesEnabled && isSubItemsVisible;
  const subItemsButton = shouldShowSubItems && !isCompact && getSubItemsButton(false);
  const showDetailsTemp = false && !isCompact; // details menu hidden for now, might be re-enabled later
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          flex: 1,
        }}
      >
        {showDetailsTemp && (
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <CardButton
              label="Linked Items"
              count={linkedItems}
              icon={<SwapCalls size={14} />}
              disabled={isPublicationMode && !linkedItems}
              onClick={() => linkedItemsHandler?.(itemID)}
            />
            <CardButton
              label="Key Dates"
              count={keyDates}
              icon={<Event size={14} />}
              disabled={isPublicationMode && !keyDates}
              onClick={() => keyDatesHandler?.(itemID)}
            />
            <CardButton
              label="Comments"
              count={comments}
              icon={<Comment size={14} />}
              disabled={isPublicationMode && !comments}
              onClick={() => commentsHandler?.(itemID)}
            />
            <CardButton
              label="Attachments"
              count={attachments}
              icon={<AttachFile size={14} />}
              disabled={isPublicationMode && !attachments}
              onClick={() => attachmentsHandler?.(itemID)}
            />
          </div>
        )}
        {subItemsButton}
      </div>
      {shouldShowSubItems && (
        <SubItems
          itemID={itemID}
          columnID={columnID}
          rowID={rowID}
          expanded={actionsExpanded}
          subItems={subItems}
          // Temporarily force readOnly to true until we implement sub-item adding functionality
          // readOnly={readOnly}
          readOnly={readOnly || true}
          maxSubItemsPerItem={maxSubItems}
          onToggleExpanded={onToggleExpanded}
          backgroundColor={subItemsBackgroundColor}
        />
      )}
    </div>
  );
}
